// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Checkbox, Typography, Skeleton } from "@aviary";
import type { IngredientFilterV2 } from "@shared/components/CatalogSearch/CatalogSearch";
import { useIngredientsQuery } from "@shared/data/queries/IngredientsQuery/Ingredients.query";
import { l } from "@shared/locales/i18n";

import * as styles from "./CommonIngredients.styles";

interface Props {
  selectedIngredients: IngredientFilterV2[];
  onSelectIngredient: (ingredientId: string, ingredientName: string) => void;
  isIngredientSelected: (ingredientId: string) => boolean;
}

const NUM_OF_INGREDIENTS = 7;

const CommonIngredients = ({
  selectedIngredients,
  onSelectIngredient,
  isIngredientSelected,
}: Props) => {
  const { t } = useTranslation();

  const { data: commonIngredientsData, loading } = useIngredientsQuery({
    variables: {
      first: NUM_OF_INGREDIENTS,
      sort: [{ direction: "DESC", field: "SEARCH_POPULARITY" }],
    },
  });

  const commonIngredients = commonIngredientsData?.viewer?.ingredients?.nodes ?? [];

  if (loading) {
    const skeletonList = Array.from({ length: NUM_OF_INGREDIENTS }).map((_, i) => (
      <div css={styles.checkbox} key={i}>
        <Skeleton type="typography" typographySize="h4" css={styles.checkboxSkeleton} />
      </div>
    ));

    return (
      <div css={styles.commonIngredientsContainer}>
        <Skeleton
          type="typography"
          typographySize="h4"
          margin="bottom"
          css={styles.headingSkeleton}
        />
        {skeletonList}
      </div>
    );
  }

  if (selectedIngredients.length >= 1) {
    return null;
  }

  const commonIngredientCheckboxes = commonIngredients.map(ingredient => (
    <div key={ingredient.id} css={styles.checkbox}>
      <Checkbox
        data-e2e={`${ingredient.name} filter`}
        isFullWidth
        onChange={() => onSelectIngredient(ingredient?.id, ingredient?.name)}
        checked={isIngredientSelected(ingredient?.id)}
      >
        {ingredient.name}
      </Checkbox>
    </div>
  ));

  return (
    <div css={styles.commonIngredientsContainer}>
      <Typography type="h5">{t(l.sharedCatalog.filter.CommonIngredients)}</Typography>
      {commonIngredientCheckboxes}
    </div>
  );
};

export { CommonIngredients };
